/**
 * Map one account of an existing bank connection to an owned account (TMMAPP-0055). It moved out of
 * the removed Bank connections section (TMMAPP-0120 item 6): My Accounts opens it from the "Finish
 * setting up {bank}" banner, and from a repair's "Map now" notice.
 */
import { useState } from 'react';
import {
  Alert,
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import type { Account } from '@/api/accounts';
import { connectorsApi, type ConnectorAccount, type ConnectorItem } from '@/api/connectors';
import { ErrorCodes, isApiError } from '@/api/errors';
import { GuardedDialog } from '@/components/GuardedDialog';
import { SubmitButton } from '@/components/SubmitButton';
import { useDialogDirtyGuard } from '@/shared/useDialogDirtyGuard';
import { useDirty } from '@/shared/useDirty';

export function MapExistingDialog({
  item,
  account,
  owned,
  takenIds,
  onClose,
  onMapped,
}: {
  item: ConnectorItem;
  account: ConnectorAccount;
  owned: Account[];
  takenIds: Set<number>;
  onClose: () => void;
  onMapped: () => void | Promise<void>;
}) {
  const { t } = useTranslation();
  const [accountId, setAccountId] = useState<number | ''>('');
  const [submitting, setSubmitting] = useState(false);
  const [alert, setAlert] = useState<string | null>(null);
  const [unavailable, setUnavailable] = useState(false);
  // The account picked is the work to keep: dirty from the first pick.
  const { dirty } = useDirty(accountId);
  const guard = useDialogDirtyGuard(dirty, onClose, submitting);
  const selected = owned.find((row) => row.id === accountId);
  const mismatch =
    selected !== undefined &&
    selected.currency !== null &&
    account.currency !== null &&
    selected.currency !== account.currency;

  async function handleMap() {
    if (accountId === '' || submitting) return;
    setSubmitting(true);
    try {
      await connectorsApi.mapAccount(item.id, account.providerAccountId, accountId);
      await onMapped();
    } catch (error) {
      if (isApiError(error) && error.code === ErrorCodes.CONNECTOR_UNAVAILABLE) {
        setUnavailable(true);
      } else {
        setAlert(t('accounts.errorGeneric'));
        guard.focusErrors();
      }
      setSubmitting(false);
    }
  }

  return (
    <GuardedDialog guard={guard} titleId="map-existing-title" fullWidth maxWidth="xs">
      <DialogTitle id="map-existing-title">{t('connections.map')}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 0.5 }}>
          {unavailable && (
            <Alert
              severity="warning"
              action={
                <Button color="inherit" size="small" onClick={() => void handleMap()}>
                  {t('common.retry')}
                </Button>
              }
            >
              {t('connections.unavailable')}
            </Alert>
          )}
          {alert !== null && <Alert severity="error">{alert}</Alert>}
          <Typography variant="body2">
            {account.name}
            {account.mask !== null ? ` · ${t('connections.mask', { mask: account.mask })}` : ''}
          </Typography>
          <FormControl fullWidth>
            <InputLabel id="map-existing-to">{t('connections.mapTo')}</InputLabel>
            <Select
              labelId="map-existing-to"
              label={t('connections.mapTo')}
              value={accountId === '' ? '' : String(accountId)}
              onChange={(event) => setAccountId(Number(event.target.value))}
            >
              {owned
                .filter((row) => !takenIds.has(row.id))
                .map((row) => (
                  <MenuItem key={row.id} value={String(row.id)}>
                    {row.name}
                    {row.currency !== null ? ` (${row.currency})` : ''}
                  </MenuItem>
                ))}
            </Select>
          </FormControl>
          {mismatch && <Alert severity="warning">{t('connections.currencyMismatch')}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={guard.requestClose}>{t('common.cancel')}</Button>
        <SubmitButton
          disabled={accountId === ''}
          loading={submitting}
          onClick={() => void handleMap()}
        >
          {t('connections.map')}
        </SubmitButton>
      </DialogActions>
    </GuardedDialog>
  );
}
